import React, { useRef, useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import {
  DATE_PRESET_LABELS,
  PRESET_ORDER,
  formatRangeLabel,
  getPresetRange,
} from '../utils/dateRangePresets';

registerLocale('ar-SA', arLocale);

/**
 * Date preset bar for Qawaem header — presets + calendar popover for « مخصص ».
 */
export default function QawaemDateRangePresetsBar({
  preset,
  range,
  onPresetChange,
  className = '',
  variant = 'header',
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const popoverRef = useRef(null);
  const header = variant === 'header';

  const btnClass = header
    ? 'shrink-0 px-2.5 py-1 rounded-md text-[11px] font-semibold leading-normal transition-all whitespace-nowrap'
    : 'shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold leading-normal transition-all whitespace-nowrap';

  const selectPreset = (id) => {
    if (id === 'custom') {
      setCustomOpen(true);
      if (!range?.start && !range?.end) {
        const month = getPresetRange('this_month');
        onPresetChange('custom', month);
      } else {
        onPresetChange('custom', range);
      }
      return;
    }
    setCustomOpen(false);
    onPresetChange(id, getPresetRange(id));
  };

  const updateCustomRange = (patch) => {
    onPresetChange('custom', {
      start: patch.start !== undefined ? patch.start : range?.start ?? null,
      end: patch.end !== undefined ? patch.end : range?.end ?? null,
    });
  };

  useEffect(() => {
    if (!customOpen) return undefined;
    const onDocClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setCustomOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [customOpen]);

  return (
    <div className={`flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 relative ${className}`}>
      <div className={`overflow-x-auto max-w-full ${header ? 'w-fit' : ''}`}>
        <div
          className={`flex flex-nowrap sm:flex-wrap gap-1.5 rounded-xl bg-forest-50/80 border border-forest-200/50 w-fit max-w-full ${
            header ? 'p-1 gap-1' : 'p-1.5'
          }`}
        >
          {PRESET_ORDER.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => selectPreset(id)}
              title={DATE_PRESET_LABELS[id]}
              className={`${btnClass} ${
                preset === id
                  ? 'qawaem-date-preset--active bg-white text-forest-900 shadow-sm ring-2 ring-forest-500/35 border border-forest-400/70'
                  : 'bg-white/80 text-forest-800 border border-forest-200/70 hover:bg-white hover:border-forest-300/80'
              }`}
            >
              {DATE_PRESET_LABELS[id]}
            </button>
          ))}
        </div>
      </div>

      <span className="inline-flex items-center gap-1.5 text-console-subtle text-xs px-2">
        <Calendar className="h-3.5 w-3.5" />
        {formatRangeLabel(range)}
      </span>

      {customOpen ? (
        <div
          ref={popoverRef}
          className="absolute top-full mt-2 right-0 z-[10001] min-w-[280px] rounded-2xl border border-forest-200/80 bg-white shadow-xl p-4 qawaem-date-custom-popover"
          dir="rtl"
        >
          <p className="text-sm font-semibold text-forest-900 mb-3 text-right">اختر الفترة</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5 text-right">من</label>
              <DatePicker
                selected={range?.start ? new Date(range.start) : null}
                onChange={(date) => updateCustomRange({ start: date })}
                selectsStart
                startDate={range?.start ? new Date(range.start) : null}
                endDate={range?.end ? new Date(range.end) : null}
                locale="ar-SA"
                dateFormat="dd/MM/yyyy"
                className="w-full rounded-xl border border-forest-200 px-3 py-2 text-sm text-forest-900 text-right"
                placeholderText="تاريخ البداية"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5 text-right">إلى</label>
              <DatePicker
                selected={range?.end ? new Date(range.end) : null}
                onChange={(date) => updateCustomRange({ end: date })}
                selectsEnd
                startDate={range?.start ? new Date(range.start) : null}
                endDate={range?.end ? new Date(range.end) : null}
                minDate={range?.start ? new Date(range.start) : null}
                locale="ar-SA"
                dateFormat="dd/MM/yyyy"
                className="w-full rounded-xl border border-forest-200 px-3 py-2 text-sm text-forest-900 text-right"
                placeholderText="تاريخ النهاية"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button
              type="button"
              onClick={() => setCustomOpen(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-forest-200 text-forest-800 bg-white hover:bg-forest-50"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={() => setCustomOpen(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-forest-700 text-white hover:bg-forest-800"
            >
              تطبيق
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
